/** Coerce API / DB values to a finite number or null. */
export function toMoney(value) {
  if (value == null || value === "") return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

function paymentStatusFromRow(row) {
  if (row.payment_status && row.payment_status !== "No payment") {
    return row.payment_status;
  }
  const pid = row.payment_id ?? row.fk_payment_id;
  if (!pid) return "No payment";
  const raw = row.raw_payment_status ?? row.payment_status;
  if (raw === 1 || raw === true || raw === "1") return "Paid";
  if (raw === 0 || raw === false || raw === "0") return "Pending";
  if (toMoney(row.payment_amount ?? row.amount) != null) return "Paid";
  return "Pending";
}

/** Normalize one timeline/summary row from API (handles legacy shapes). */
export function normalizeTimelineRow(row) {
  if (!row || typeof row !== "object") return row;

  const paymentId = row.payment_id ?? row.fk_payment_id ?? null;
  const paymentAmount = toMoney(
    row.payment_amount ?? row.amount ?? row.membership_fee
  );
  const planPrice = toMoney(row.plan_price ?? row.planPrice);

  return {
    ...row,
    plan_name: row.plan_name || row.planName || row.plan || null,
    plan_price: planPrice,
    payment_id: paymentId,
    payment_amount: paymentAmount,
    payment_status: paymentStatusFromRow({ ...row, payment_amount: paymentAmount }),
    payment_verified:
      row.payment_verified != null
        ? Boolean(row.payment_verified)
        : row.paymentVerified != null
          ? Boolean(row.paymentVerified)
          : null,
    receipt_no: row.receipt_no || row.receiptNo || null,
    transaction_id: row.transaction_id || row.transactionId || null,
    payment_date: row.payment_date || row.paymentDate || row.date || null,
    payment_mode: row.payment_mode || row.paymentMode || null,
    membership_fee: paymentAmount ?? planPrice,
  };
}

/** Attach tbl_payments rows to timeline entries by payment_id. */
export function mergePaymentsIntoTimeline(timeline, payments) {
  const list = Array.isArray(timeline) ? timeline : [];
  const payList = Array.isArray(payments) ? payments : [];
  if (!payList.length) {
    return list.map(normalizeTimelineRow);
  }

  const byId = new Map();
  for (const p of payList) {
    if (p?.id != null) byId.set(Number(p.id), p);
  }

  return list.map((row) => {
    const base = normalizeTimelineRow(row);
    const pid = base.payment_id != null ? Number(base.payment_id) : null;
    const pay = pid ? byId.get(pid) : null;
    if (!pay) return base;

    const amount = toMoney(pay.amount ?? pay.payment_amount);
    const paid =
      pay.payment_status === 1 ||
      pay.payment_status === true ||
      pay.payment_status === "1" ||
      pay.status === "Success" ||
      amount != null;

    return normalizeTimelineRow({
      ...base,
      payment_id: pid,
      payment_amount: amount ?? base.payment_amount,
      raw_payment_status: pay.payment_status,
      payment_status: paid ? "Paid" : "Pending",
      payment_verified:
        pay.payment_verified != null ? Boolean(pay.payment_verified) : base.payment_verified,
      receipt_no: pay.receipt_no || base.receipt_no,
      transaction_id: pay.transaction_id || base.transaction_id,
      payment_date: pay.payment_date || pay.date || base.payment_date,
      payment_mode: pay.payment_mode || pay.mode_name || base.payment_mode,
      plan_name: base.plan_name || pay.plan || null,
      plan_price: base.plan_price ?? toMoney(pay.plan_price),
    });
  });
}

export function normalizeMembershipOverview(overview, payments = []) {
  if (!overview || typeof overview !== "object") return overview;

  const timeline = mergePaymentsIntoTimeline(overview.timeline, payments);
  let summary = overview.summary
    ? normalizeTimelineRow({
        ...overview.summary,
        membership_id: overview.summary.membership_id,
        membership_type: overview.summary.membership_type,
        start_date: overview.summary.membership_start_date,
        end_date: overview.summary.membership_end_date,
        status: overview.summary.validity_status,
        payment_amount: overview.summary.payment_amount,
        plan_price: overview.summary.plan_price,
        payment_id: overview.summary.payment_id,
        payment_status: overview.summary.payment_status,
        payment_verified: overview.summary.payment_verified,
        receipt_no: overview.summary.receipt_no,
        transaction_id: overview.summary.transaction_id,
        payment_date: overview.summary.payment_date,
        payment_mode: overview.summary.payment_mode,
        renewal_status: overview.summary.renewal_status,
      })
    : null;

  if (summary) {
    const match =
      timeline.find((t) => t.membership_id === overview.summary.membership_id) ||
      timeline[0];
    if (match) {
      summary = {
        ...overview.summary,
        plan_name: overview.summary.plan_name || match.plan_name,
        plan_price: overview.summary.plan_price ?? match.plan_price,
        payment_id: overview.summary.payment_id ?? match.payment_id,
        payment_amount: overview.summary.payment_amount ?? match.payment_amount,
        membership_fee:
          overview.summary.membership_fee ??
          match.payment_amount ??
          match.plan_price,
        payment_status:
          overview.summary.payment_status &&
          overview.summary.payment_status !== "No payment"
            ? overview.summary.payment_status
            : match.payment_status,
        payment_verified: overview.summary.payment_verified ?? match.payment_verified,
        receipt_no: overview.summary.receipt_no || match.receipt_no,
        transaction_id: overview.summary.transaction_id || match.transaction_id,
        payment_date: overview.summary.payment_date || match.payment_date,
        payment_mode: overview.summary.payment_mode || match.payment_mode,
      };
    }
  }

  return { ...overview, timeline, summary };
}
